import { Component } from '@theme/component';

/**
 * Lower header sub-nav with goods/gravure tab persistence via localStorage.
 * Relies on Liquid for page-kind attributes; reapplies state on Section Rendering
 * morph via updatedCallback.
 *
 * Persistence is page-open only (persistIfNeeded). Tab clicks navigate away;
 * the destination page saves its own kind — click-time setStored caused mis-writes.
 *
 * Cross-origin account hub links get `ymc_tab` query so the hub can restore the
 * active tab (localStorage cannot be shared across domains).
 *
 * @extends {Component}
 */
class YmcHeaderNav extends Component {
  /** @type {boolean} */
  #persisted = false;

  /** @type {boolean} 今DOMに存在し表示中か。外れたら false */
  #alive = false;

  /** @type {EventListener | null} */
  #onAccountLinkClick = null;

  connectedCallback() {
    super.connectedCallback();
    this.#alive = true;
    this.applyState();

    // 後から描画された account リンクにもクリック直前で ymc_tab を付ける
    this.#onAccountLinkClick = (event) => {
      const target = /** @type {EventTarget | null} */ (event.target);
      const anchor =
        target instanceof Element
          ? target.closest('a[data-ymc-account-link], a.account-button')
          : null;
      if (!anchor) return;
      this.#syncAccountLinkParams(this.resolveTab());
    };
    document.addEventListener('click', this.#onAccountLinkClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#alive = false;
    if (this.#onAccountLinkClick) {
      document.removeEventListener('click', this.#onAccountLinkClick, true);
      this.#onAccountLinkClick = null;
    }
  }

  /**
   * Called after Section Rendering API morph (see morph.js onAfterUpdate).
   */
  updatedCallback() {
    super.updatedCallback();
    this.applyState();
  }

  /**
   * @returns {boolean}
   */
  #isNeutralPage() {
    let isNeutral = this.getAttribute('data-ymc-is-neutral') === 'true';
    const pageKind = this.getAttribute('data-ymc-page-kind') || '';
    if (pageKind === 'neutral') isNeutral = true;
    return isNeutral;
  }

  /**
   * @returns {string | null}
   */
  getStored() {
    try {
      return localStorage.getItem('ymc_active_tab');
    } catch (e) {
      return null;
    }
  }

  /**
   * @param {string} value
   * 中立ページでは書き込まない（直前の系統を保持するため）
   */
  setStored(value) {
    if (this.#isNeutralPage()) return;
    try {
      localStorage.setItem('ymc_active_tab', value);
    } catch (e) {
      /* ignore quota / private mode */
    }
  }

  /**
   * goods/gravure ページのとき、コンポーネント生存中に初回1回だけ localStorage へ保存する。
   * 中立ページ・不明ページ・DOMから外れたインスタンスでは保存しない。
   * 保存経路はこのメソッドのみ。
   */
  persistIfNeeded() {
    if (!this.#alive) return;
    if (this.#persisted) return;
    if (this.#isNeutralPage()) return;

    const pageKind = this.getAttribute('data-ymc-page-kind') || 'goods';
    if (pageKind === 'goods' || pageKind === 'gravure') {
      this.setStored(pageKind);
      this.#persisted = true;
    }
  }

  /**
   * @returns {'goods' | 'gravure'}
   */
  resolveTab() {
    if (this.#isNeutralPage()) {
      const stored = this.getStored();
      return stored === 'goods' || stored === 'gravure' ? stored : 'goods';
    }

    const pageKind = this.getAttribute('data-ymc-page-kind') || 'goods';
    if (pageKind === 'goods' || pageKind === 'gravure') {
      return pageKind;
    }

    return 'goods';
  }

  /**
   * account hub 向けリンクへ現在タブをクエリで付与する（別オリジン引き継ぎ用）。
   * localStorage は触らない。
   * @param {'goods' | 'gravure'} tab
   */
  #syncAccountLinkParams(tab) {
    if (tab !== 'goods' && tab !== 'gravure') return;

    document
      .querySelectorAll('a[data-ymc-account-link], a.account-button')
      .forEach((anchor) => {
        const href = anchor.getAttribute('href');
        if (!href) return;

        try {
          const url = new URL(href, window.location.origin);
          // 同一オリジンの相対 /account はストア内導線のため対象外
          if (url.origin === window.location.origin) return;
          if (!url.pathname.includes('/account')) return;

          url.searchParams.set('ymc_tab', tab);
          anchor.setAttribute('href', url.toString());
        } catch (e) {
          /* invalid href */
        }
      });
  }

  applyState() {
    this.persistIfNeeded();
    const tab = this.resolveTab();

    // 状態を <html> のクラスに反映（ヘッダー差し替えで消えない場所）
    const root = document.documentElement;
    if (tab === 'gravure') {
      root.classList.add('ymc-tab-gravure');
      root.classList.remove('ymc-tab-goods');
    } else {
      root.classList.add('ymc-tab-goods');
      root.classList.remove('ymc-tab-gravure');
    }

    // 補助：従来の属性・タブハイライト（htmlクラス基準CSSが主軸）
    if (this.getAttribute('data-ymc-active-tab') !== tab) {
      this.setAttribute('data-ymc-active-tab', tab);
    }
    this.querySelectorAll('[data-ymc-tab]').forEach((tabEl) => {
      const on = tabEl.getAttribute('data-ymc-tab') === tab;
      tabEl.classList.toggle('is-active', on);
      if (on) {
        tabEl.setAttribute('aria-current', 'page');
      } else {
        tabEl.removeAttribute('aria-current');
      }
    });

    this.#syncAccountLinkParams(tab);
  }
}

if (!customElements.get('ymc-header-nav')) {
  customElements.define('ymc-header-nav', YmcHeaderNav);
}
